import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Clock, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { BRAND, waLink, GOOGLE_BUSINESS_URL, GOOGLE_REVIEW_URL } from "../config";
import { Reveal, Page } from "../components/Reveal";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { WhatsAppIcon, InstagramIcon, GoogleIcon } from "../components/icons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contato = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const googleClick = (e) => {
    if (!GOOGLE_REVIEW_URL && !GOOGLE_BUSINESS_URL) {
      e.preventDefault();
      toast(t.contactPage.googleNote);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error(t.contactPage.form.required);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      toast.success(t.contactPage.form.success);
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error(t.contactPage.form.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "h-12 w-full rounded-2xl border border-blush bg-white px-5 text-[15px] text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-rose";

  return (
    <Page>
      <SEO title={t.meta.contato.title} desc={t.meta.contato.desc} />
      <PageHero eyebrow={t.contactPage.eyebrow} title={t.contactPage.title} desc={t.contactPage.p} testid="contact-hero" />

      <section className="bg-cream pb-24 sm:pb-32" data-testid="contact-content">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1fr_1.1fr]">
          <div>
            <Reveal>
              <div className="img-zoom overflow-hidden rounded-3xl shadow-lg">
                <img src="/assets/salon-facade.jpg" alt="Fachada da Bloom Beauty Concept Hair" className="h-64 w-full object-cover" loading="lazy" data-testid="contact-facade" />
              </div>
            </Reveal>

            <Reveal delay={0.1} className="mt-8 space-y-6">
              <div className="flex gap-4">
                <MapPin className="mt-1 w-5 h-5 shrink-0 text-rose" strokeWidth={1.5} />
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-rosedeep">{t.contactPage.addressTitle}</p>
                  <address className="mt-1.5 not-italic leading-relaxed text-ink/75" data-testid="contact-address">
                    {BRAND.addressLines.map((l) => (
                      <span key={l} className="block">{l}</span>
                    ))}
                  </address>
                </div>
              </div>
              <div className="flex gap-4">
                <Phone className="mt-1 w-5 h-5 shrink-0 text-rose" strokeWidth={1.5} />
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-rosedeep">{t.contactPage.phoneTitle}</p>
                  <a href={`tel:+${BRAND.phoneIntl}`} className="mt-1.5 block text-ink/75 hover:text-rosedeep" data-testid="contact-phone">
                    {BRAND.phoneDisplay}
                  </a>
                  <a href={BRAND.instagramUrl} target="_blank" rel="noopener noreferrer" className="block text-ink/75 hover:text-rosedeep" data-testid="contact-instagram">
                    {BRAND.instagram}
                  </a>
                </div>
              </div>
              <div className="flex gap-4">
                <Clock className="mt-1 w-5 h-5 shrink-0 text-rose" strokeWidth={1.5} />
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-rosedeep">{t.contactPage.hoursTitle}</p>
                  <p className="mt-1.5 text-ink/75" data-testid="contact-hours">
                    {t.contactPage.hoursWeek}: {t.contactPage.hoursWeekTime}
                    <br />
                    {t.contactPage.hoursSat}: {t.contactPage.hoursSatTime}
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15} className="mt-9 grid gap-3">
              <a
                href={waLink(t.waDefault)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="flex items-center justify-center gap-3 rounded-full bg-rosedeep px-6 py-4 text-[12px] font-bold uppercase tracking-[0.16em] text-white transition-all duration-300 hover:bg-ink"
              >
                <WhatsAppIcon className="w-5 h-5" /> WhatsApp
              </a>
              <div className="grid grid-cols-2 gap-3">
                <a
                  href={BRAND.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-btn"
                  className="flex items-center justify-center gap-2 rounded-full border border-rosedeep px-5 py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] text-rosedeep transition-all duration-300 hover:bg-rosedeep hover:text-white"
                >
                  <InstagramIcon className="w-4.5 h-4.5 w-[18px] h-[18px]" /> Instagram
                </a>
                <a
                  href={BRAND.mapsQuery}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions-btn"
                  className="flex items-center justify-center gap-2 rounded-full border border-gold/60 px-5 py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] text-ink transition-all duration-300 hover:bg-gold/15"
                >
                  <MapPin className="w-[18px] h-[18px]" /> {t.common.directions}
                </a>
              </div>
              <div className="rounded-3xl border border-gold/25 bg-white p-6" data-testid="google-card">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-cream text-rosedeep ring-1 ring-gold/30">
                    <GoogleIcon className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-ink">{t.contactPage.googleTitle}</p>
                    <p className="text-[13px] font-semibold tracking-wide text-gold" data-testid="google-rating">
                      {t.contactPage.googleRating}
                    </p>
                  </div>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-ink/55" data-testid="google-pending-note">
                  {t.contactPage.googleNote}
                </p>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <a
                    href={GOOGLE_BUSINESS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="google-reviews-btn"
                    className="flex items-center justify-center gap-2 rounded-full border border-blush px-4 py-3 text-[11px] font-bold uppercase tracking-[0.12em] text-ink transition-all duration-300 hover:border-rose/60 hover:bg-blush/40"
                  >
                    {t.contactPage.viewReviews}
                  </a>
                  <a
                    href={GOOGLE_REVIEW_URL || GOOGLE_BUSINESS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-google-btn"
                    className="flex items-center justify-center gap-2 rounded-full bg-rosedeep px-4 py-3 text-[11px] font-bold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:bg-ink"
                  >
                    {t.contactPage.leaveReview}
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-[2rem] bg-white p-7 shadow-[0_25px_60px_-30px_rgba(142,75,92,0.35)] ring-1 ring-blush sm:p-10" data-testid="contact-form">
              <h2 className="font-serif text-2xl font-semibold text-ink sm:text-3xl">{t.contactPage.formTitle}</h2>
              <div className="mt-4 h-px w-14 bg-gold" />
              <div className="mt-7 grid gap-4">
                <div>
                  <label htmlFor="cf-name" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-ink/55">
                    {t.contactPage.form.name} *
                  </label>
                  <input id="cf-name" data-testid="form-name" className={inputCls} placeholder={t.contactPage.form.namePh}
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={120} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="cf-phone" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-ink/55">
                      {t.contactPage.form.phone}
                    </label>
                    <input id="cf-phone" data-testid="form-phone" type="tel" className={inputCls} placeholder={t.contactPage.form.phonePh}
                      value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} maxLength={40} />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-ink/55">
                      {t.contactPage.form.email}
                    </label>
                    <input id="cf-email" data-testid="form-email" type="email" className={inputCls} placeholder={t.contactPage.form.emailPh}
                      value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} maxLength={160} />
                  </div>
                </div>
                <div>
                  <label htmlFor="cf-service" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-ink/55">
                    {t.contactPage.form.service}
                  </label>
                  <select id="cf-service" data-testid="form-service" className={inputCls}
                    value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}>
                    <option value="">—</option>
                    {t.contactPage.serviceOptions.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="cf-message" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-ink/55">
                    {t.contactPage.form.message} *
                  </label>
                  <textarea id="cf-message" data-testid="form-message" rows={4} placeholder={t.contactPage.form.messagePh}
                    className="w-full rounded-2xl border border-blush bg-white px-5 py-3.5 text-[15px] text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-rose"
                    value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required maxLength={2000} />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="form-submit"
                  className="group mt-2 flex items-center justify-center gap-2.5 rounded-full bg-rosedeep px-7 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white shadow-[0_18px_40px_-14px_rgba(168,94,112,0.8)] transition-all duration-300 hover:bg-ink disabled:opacity-60"
                >
                  {sending ? t.contactPage.form.sending || t.common.sending : t.contactPage.form.submit}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </Page>
  );
};

export default Contato;
